
import { useEffect, useRef, useState } from 'react';

export default function Home() {
  const [scrollY, setScrollY] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);
  const parallaxRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      title: "IT Consulting",
      icon: "ri-lightbulb-line",
      description: "Strategic technology guidance to optimize your business operations and drive digital transformation.",
      features: ["Technology Strategy", "Digital Transformation", "System Architecture", "Performance Optimization"],
      color: "from-blue-600 to-purple-600"
    },
    {
      title: "IT Helpdesk and Technical Support", 
      icon: "ri-customer-service-line",
      description: "24/7 comprehensive technical support ensuring your systems run smoothly and efficiently.",
      features: ["24/7 Support", "Remote Assistance", "Hardware Troubleshooting", "Software Solutions"],
      color: "from-green-600 to-teal-600"
    },
    {
      title: "Managed IT Services",
      icon: "ri-settings-3-line", 
      description: "Complete IT infrastructure management and monitoring to keep your business running seamlessly.",
      features: ["Proactive Monitoring", "Security Management", "Backup Solutions", "Network Administration"],
      color: "from-orange-600 to-red-600"
    },
    {
      title: "Business Continuity Planning",
      icon: "ri-shield-check-line",
      description: "Comprehensive disaster recovery and business continuity strategies to protect your operations.",
      features: ["Disaster Recovery", "Risk Assessment", "Backup Strategies", "Emergency Response"],
      color: "from-purple-600 to-pink-600"
    },
    {
      title: "IT Training and Workshops",
      icon: "ri-graduation-cap-line",
      description: "Expert-led training programs to enhance your team's technical skills and productivity.",
      features: ["Custom Training", "Certification Prep", "Skills Development", "Interactive Workshops"],
      color: "from-teal-600 to-cyan-600"
    }
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-gray-950">
      {/* Parallax Background Layers */}
      <div className="fixed inset-0 z-0">
        <div 
          ref={(el) => (parallaxRef.current[0] = el)}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=futuristic%20technology%20cityscape%20with%20glowing%20blue%20neural%20networks%20and%20digital%20data%20streams%20flowing%20through%20modern%20glass%20buildings%20at%20night%2C%20cyberpunk%20aesthetic%2C%20high-tech%20atmosphere%2C%204K%20vertical%20composition&width=1080&height=4320&seq=bg1&orientation=portrait)`,
            transform: `translateY(${scrollY * 0.3}px)`
          }}
        />
        <div 
          ref={(el) => (parallaxRef.current[1] = el)}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-15"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=abstract%20digital%20matrix%20with%20flowing%20code%20lines%20and%20geometric%20patterns%20in%20deep%20blue%20and%20purple%20gradients%2C%20vertical%20tech%20background%2C%20modern%20computing%20visualization&width=1080&height=4320&seq=bg2&orientation=portrait)`,
            transform: `translateY(${-scrollY * 0.2}px)`
          }}
        />
        <div 
          ref={(el) => (parallaxRef.current[2] = el)}
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=ethereal%20particle%20systems%20and%20light%20beams%20creating%20a%20vertical%20technological%20landscape%20with%20holographic%20elements%20and%20floating%20data%20nodes%20in%20space&width=1080&height=4320&seq=bg3&orientation=portrait)`,
            transform: `translateY(${scrollY * 0.1}px)`
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="text-center max-w-6xl mx-auto">
            <div 
              className="inline-block p-8 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border border-blue-500/30 mb-8 transform hover:scale-110 transition-all duration-700 hover:rotate-3"
              style={{
                transform: `translateX(${(mousePosition.x - window.innerWidth / 2) * 0.02}px) translateY(${(mousePosition.y - window.innerHeight / 2) * 0.02}px)`
              }}
            >
              <i className="ri-computer-line text-6xl sm:text-7xl lg:text-8xl text-blue-400"></i>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 leading-tight">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-teal-400 bg-clip-text text-transparent">
                Consulting & Support
              </span>
              <br />
              <span className="text-white/90">Services</span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Empowering your business with comprehensive IT solutions, expert consulting, 
              and 24/7 support to drive innovation and growth.
            </p>
          </div>
        </section>

        {/* Services Sections */}
        {services.map((service, index) => (
          <section 
            key={service.title}
            ref={(el) => (sectionsRef.current[index] = el)}
            className="min-h-screen flex items-center px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-16 sm:py-20 lg:py-24"
          >
            <div className="max-w-7xl mx-auto w-full">
              <div className={`grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-5 gap-8 lg:gap-12 xl:gap-16 items-center ${index % 2 === 0 ? '' : 'lg:flex-row-reverse'}`}>
                {/* Content */}
                <div className={`xl:col-span-3 ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'} space-y-6 sm:space-y-8`}>
                  <div className="group cursor-pointer">
                    <div className={`inline-flex items-center space-x-4 p-4 sm:p-6 rounded-2xl bg-gradient-to-r ${service.color}/10 backdrop-blur-sm border border-white/10 hover:border-white/30 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/20`}>
                      <div className={`p-3 sm:p-4 rounded-xl bg-gradient-to-r ${service.color} transform group-hover:rotate-12 transition-all duration-500`}>
                        <i className={`${service.icon} text-2xl sm:text-3xl lg:text-4xl text-white`}></i>
                      </div>
                      <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white group-hover:text-blue-400 transition-colors duration-300">
                        {service.title}
                      </h2>
                    </div>
                  </div>

                  <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 leading-relaxed">
                    {service.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {service.features.map((feature, featureIndex) => (
                      <div 
                        key={feature}
                        className="group flex items-center space-x-3 p-4 sm:p-6 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-blue-400/50 hover:bg-white/10 transition-all duration-300 hover:scale-105 cursor-pointer"
                        style={{
                          animationDelay: `${featureIndex * 0.1}s`
                        }}
                      >
                        <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-teal-400 group-hover:scale-150 transition-transform duration-300"></div>
                        <span className="text-base sm:text-lg lg:text-xl text-gray-200 group-hover:text-white transition-colors duration-300">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <button className={`group relative overflow-hidden px-8 sm:px-10 lg:px-12 py-4 sm:py-5 lg:py-6 rounded-full bg-gradient-to-r ${service.color} text-white font-semibold text-base sm:text-lg lg:text-xl transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-500 whitespace-nowrap cursor-pointer`}>
                    <span className="relative z-10 flex items-center space-x-2">
                      <span>Learn More</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  </button>
                </div>

                {/* 3D Visual */}
                <div className={`xl:col-span-2 ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'} relative`}>
                  <div className="relative group cursor-pointer">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-700"></div>
                    <div className="relative overflow-hidden rounded-3xl border border-white/20 hover:border-white/40 transition-all duration-500">
                      <img 
                        src={`https://readdy.ai/api/search-image?query=$%7BgetImagePrompt%28service.title%29%7D&width=600&height=800&seq=service${index}&orientation=portrait`}
                        alt={service.title}
                        className="w-full h-[500px] sm:h-[600px] lg:h-[700px] xl:h-[800px] object-cover object-top transform group-hover:scale-110 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-transparent to-transparent"></div>
                      
                      {/* Floating 3D Elements */}
                      <div className="absolute top-8 right-8 w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 flex items-center justify-center">
                        <div className={`w-full h-full rounded-xl bg-gradient-to-r ${service.color} backdrop-blur-sm border border-white/30 flex items-center justify-center transform group-hover:rotate-45 group-hover:scale-110 transition-all duration-700 animate-pulse`}>
                          <i className={`${service.icon} text-2xl sm:text-3xl lg:text-4xl text-white`}></i>
                        </div>
                      </div>

                      {/* Interactive Particles */}
                      <div className="absolute inset-0 pointer-events-none">
                        {[...Array(6)].map((_, i) => (
                          <div
                            key={i}
                            className="absolute w-2 h-2 bg-blue-400 rounded-full opacity-30 group-hover:opacity-100 transition-all duration-500"
                            style={{
                              left: `${20 + i * 15}%`,
                              top: `${10 + i * 12}%`,
                              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                              animationDelay: `${i * 0.2}s`
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* CTA Section */}
        <section className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16 py-16 sm:py-20 lg:py-24">
          <div className="text-center max-w-6xl mx-auto relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-teal-600/10 rounded-3xl blur-3xl"></div>
            <div className="relative p-8 sm:p-12 lg:p-16 xl:p-20 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/20 hover:border-white/40 transition-all duration-500">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 sm:mb-8">
                Ready to <span className="bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent">Transform</span> Your Business?
              </h2>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mb-8 sm:mb-12 max-w-4xl mx-auto leading-relaxed">
                Let our expert team guide your digital transformation journey with cutting-edge solutions and unparalleled support.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-center items-center">
                <button className="group relative overflow-hidden px-10 sm:px-12 lg:px-16 py-5 sm:py-6 lg:py-7 rounded-full bg-gradient-to-r from-blue-600 to-teal-600 text-white font-semibold text-lg sm:text-xl lg:text-2xl transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/30 transition-all duration-500 whitespace-nowrap cursor-pointer">
                  <span className="relative z-10 flex items-center space-x-3">
                    <span>Get Started Today</span>
                    <i className="ri-rocket-line group-hover:translate-x-2 group-hover:-translate-y-1 transition-transform duration-300"></i>
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </button>
                <button className="group px-10 sm:px-12 lg:px-16 py-5 sm:py-6 lg:py-7 rounded-full border-2 border-white/30 text-white font-semibold text-lg sm:text-xl lg:text-2xl hover:border-blue-400 hover:bg-blue-400/10 hover:text-blue-400 transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer">
                  <span className="flex items-center space-x-3">
                    <span>Schedule Consultation</span>
                    <i className="ri-calendar-line group-hover:scale-110 transition-transform duration-300"></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 640px) {
          .grid-cols-1 {
            gap: 1rem;
          }
        }
        
        @media (min-width: 2560px) {
          .max-w-7xl {
            max-width: 120rem;
          }
        }
      `}</style>
    </div>
  );
}

function getImagePrompt(serviceTitle: string): string {
  const prompts = {
    "IT Consulting": "professional business consultant working with holographic technology displays and digital transformation concepts in a modern office environment with glass surfaces and blue ambient lighting, futuristic consulting atmosphere",
    "IT Helpdesk and Technical Support": "advanced technical support center with multiple monitors displaying system diagnostics and network monitoring tools, cybersecurity elements, professional helpdesk environment with glowing blue and green interface elements",  
    "Managed IT Services": "sophisticated data center with server racks and network infrastructure, managed services visualization with monitoring dashboards and automated systems, high-tech server room with blue LED lighting and cable management",
    "Business Continuity Planning": "robust security and disaster recovery visualization with backup systems and redundant infrastructure, business continuity planning with shield symbols and protection elements in a secure facility environment",
    "IT Training and Workshops": "modern training facility with interactive displays and learning technology, professional development environment with computers and presentation screens, educational technology setup with collaborative workspace design"
  };
  
  return prompts[serviceTitle as keyof typeof prompts] || "modern technology workspace with digital elements and professional atmosphere";
}
